import { Column, GridRow } from '@shared';
import { CurrencyPipe, DatePipe, PercentPipe } from '@angular/common';
import { IChangeset } from '@shared-data';

class PricingChangeset implements IChangeset {
  id: string;
  restaurantId: string;
  categoryId?: string;
  isALaCarte?: boolean;
  isCombo?: boolean;
  status?: string;

  @Column({
    field: 'categoryName',
    title: 'Category',
    order: 0,
    width: 150,
    sortable: true,
    hidden: true,
    filterable: true,
    filterType: 'text',
    align: 'left',
  })
  categoryName?: string;

  @Column({
    field: 'restaurantName',
    title: 'Restaurant',
    order: 0.1,
    width: 80,
    sortable: true,
    visibleInDetailsView: false,
    visibleInQuickView: false,
  })
  restaurantName?: string;

  @Column({
    field: 'tierName',
    title: 'Tier',
    order: 0,
    width: 160,
    sortable: true,
    visibleInDetailsView: false,
    visibleInQuickView: true,
    align: 'left',
  })
  tierName?: string;

  tierLab?: string;

  @Column({
    field: 'plu',
    title: 'PLU',
    order: 1.9,
    width: 60,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    sortable: true,
    visibleInQuickView: true,
  })
  plu?: string;

  @Column({
    field: 'parItemId',
    title: 'Brink ID',
    order: 2,
    width: 80,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    sortable: true,
    visibleInQuickView: true,
  })
  parItemId?: string;

  @Column({
    field: 'itemName',
    title: 'Product',
    order: 1,
    width: 200,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  itemName: string;

  itemDescription?: string;

  @Column({
    field: 'isActive',
    title: 'Is Active',
    order: 1.2,
    width: 80,
    filterable: true,
    filterType: 'boolean',
    type: 'boolean',
    sortable: true,
    visibleInQuickView: false,
    filterOptions: [
      { name: 'Yes', value: true },
      { name: 'No', value: false },
    ],
  })
  isActive: boolean;

  @Column({
    field: 'itemPrice',
    title: 'Current Price',
    order: 3,
    pipeToken: CurrencyPipe,
    width: 80,
    filterable: true,
    filterType: 'numeric',
    sortable: true,
    visibleInQuickView: true,
  })
  itemPrice?: number;

  @Column({
    field: 'itemPlateCost', hideForConcept: ['BK_CA'],
    title: 'Current Plate Cost',
    order: 4,
    pipeToken: CurrencyPipe,
    width: 80,
    filterType: 'numeric',
    filterable: true,
    sortable: true,
  })
  itemPlateCost?: number;
  bkpn?: number;

  @Column({
    field: 'deliveryPrice',
    title: 'Delivery Price',
    order: 10,
    pipeToken: CurrencyPipe,
    width: 80,
    filterType: 'numeric',
    filterable: true,
    sortable: true,
    visibleInQuickView: true,
  })
  deliveryPrice?: number;

  @Column({
    field: 'rbiSuggestedPrice',
    title: 'RBI Recomm.',
    order: 6,
    pipeToken: CurrencyPipe,
    width: 80,
    filterType: 'numeric',
    filterable: true,
    sortable: true,
  })
  rbiSuggestedPrice?: number;

  @Column({
    field: 'priceCap',
    title: 'Price Cap',
    order: 6.5,
    pipeToken: CurrencyPipe,
    width: 80,
    filterType: 'numeric',
    filterable: true,
    sortable: true,
  })
  priceCap?: number;

  @Column({
    field: 'grossProfitPercentage', hideForConcept: ['BK_CA'],
    title: 'Current GP%',
    order: 5,
    pipeToken: PercentPipe,
    width: 80,
    filterType: 'percentage',
    filterable: true,
    sortable: true,
  })
  grossProfitPercentage?: number;

  @Column({
    field: 'grossProfitDollars', hideForConcept: ['BK_CA'],
    title: 'Current GP$',
    order: 5,
    pipeToken: CurrencyPipe,
    width: 80,
    filterType: 'numeric',
    filterable: true,
    sortable: true,
  })
  grossProfitDollars?: number;

  @Column({
    field: 'newDeliveryPrice',
    title: 'New Delivery Price',
    order: 11,
    editable: true,
    type: 'number',
    pipeToken: CurrencyPipe,
    width: 100,
    filterType: 'numeric',
    filterable: true,
    visibleInQuickView: true,
  })
  newDeliveryPrice?: number;

  @Column({
    field: 'newPrice',
    title: 'New Price',
    order: 7,
    type: 'number',
    editable: true,
    pipeToken: CurrencyPipe,
    width: 80,
    filterType: 'numeric',
    filterable: true,
    visibleInQuickView: true,
  })
  newPrice?: number;

  @Column({
    field: 'pricingScheduledDate',
    hidden: true,
    title: 'Price Change Scheduled Date',
    order: 9,
    pipeToken: DatePipe,
    pipeArgs: ['MM/dd/yyyy hh:mm a'],
    type: 'date',
    width: 170,
    filterType: 'date',
    filterable: true,
  })
  pricingScheduledDate: Date | null;

  availabilityScheduledDate: Date | null;

  @Column({
    field: 'deliveryUpchargePercentage',
    title: 'Delivery Upcharge %',
    order: 11,
    pipeToken: PercentPipe,
    filterable: true,
    filterType: 'percentage',
    sortable: true,
    width: 90,
    hidden: true,
  })
  deliveryUpchargePercentage?: number;

  @Column({
    field: 'newGrossProfitPercentage', hideForConcept: ['BK_CA'],
    title: 'New GP%',
    order: 8,
    pipeToken: PercentPipe,
    width: 80,
    filterType: 'percentage',
    filterable: true,
    sortable: true,
  })
  newGrossProfitPercentage?: number =
    this.newGrossProfitPercentage ??
    (this.newPrice && this.itemPlateCost
      ? (this.newPrice - this.itemPlateCost) / this.newPrice
      : undefined);

  @Column({
    field: 'newGrossProfitDollars', hideForConcept: ['BK_CA'],
    title: 'New GP$',
    order: 8,
    pipeToken: CurrencyPipe,
    width: 80,
    filterType: 'numeric',
    filterable: true,
    sortable: true,
  })
  newGrossProfitDollars?: number =
    this.newGrossProfitDollars ??
    (this.newPrice && this.itemPlateCost
      ? (this.newPrice - this.itemPlateCost) / this.newPrice
      : undefined);

  isRealignment: boolean = false;

  type: string = 'pricing';

  @Column({
    field: 'lastModPricingInstore',
    hidden: true,
    title: 'Last Modified Pricing In-Store',
    order: 12,
    pipeToken: DatePipe,
    pipeArgs: ['MM/dd/yyyy hh:mm a'],
    type: 'date',
    width: 170,
    filterType: 'date',
    filterable: true,
  })
  lastModPricingInstore?: Date;

  @Column({
    field: 'lastModPricingDelivery',
    hidden: true,
    title: 'Last Modified Pricing Delivery',
    order: 13,
    pipeToken: DatePipe,
    pipeArgs: ['MM/dd/yyyy hh:mm a'],
    type: 'date',
    width: 170,
    filterType: 'date',
    filterable: true,
  })
  lastModPricingDelivery?: Date;

  isDeliveryAvailable?: boolean;

  constructor(changeset?: Partial<PricingChangeset>) {
    this.id = changeset?.id || '';
    this.itemName = changeset?.itemName || '';
    this.tierName = changeset?.tierName || '';
    this.itemDescription = changeset?.itemDescription;
    this.plu = changeset?.plu;
    this.parItemId = changeset?.parItemId;
    this.restaurantId = changeset?.restaurantId || '';
    this.restaurantName = changeset?.restaurantName || '';
    this.itemPrice = changeset?.itemPrice;
    this.grossProfitDollars = changeset?.grossProfitDollars;
    this.grossProfitPercentage = changeset?.grossProfitPercentage;
    this.rbiSuggestedPrice = changeset?.rbiSuggestedPrice;
    this.priceCap = changeset?.priceCap;
    this.itemPlateCost = changeset?.itemPlateCost;
    this.deliveryPrice = changeset?.deliveryPrice;
    this.pricingScheduledDate = changeset?.pricingScheduledDate
      ? new Date(changeset.pricingScheduledDate)
      : null;
    this.availabilityScheduledDate = changeset?.availabilityScheduledDate
      ? new Date(changeset.availabilityScheduledDate)
      : null;
    this.newDeliveryPrice = changeset?.newDeliveryPrice;
    this.newPrice = changeset?.newPrice;
    this.newGrossProfitDollars = changeset?.newGrossProfitDollars;
    this.newGrossProfitPercentage = changeset?.newGrossProfitPercentage;
    this.isActive = changeset?.isActive || false;
    this.isALaCarte = changeset?.isALaCarte || true;
    this.isCombo = changeset?.isCombo || false;
    this.tierLab = changeset?.tierLab || '';
    this.categoryName = changeset?.categoryName || '';
    this.deliveryUpchargePercentage =
      changeset?.deliveryUpchargePercentage || 0;
    this.isRealignment = changeset?.isRealignment || false;
    this.status = changeset?.status;
    this.lastModPricingDelivery = changeset?.lastModPricingDelivery;
    this.lastModPricingInstore = changeset?.lastModPricingInstore;
    this.isDeliveryAvailable = changeset?.isDeliveryAvailable;
  }
}

export class GridRowPricingChangeset extends GridRow(PricingChangeset) {}
